import React, { useEffect, useMemo, useState } from 'react';
import { SpinnerSize } from '../../../../ui-toolkit';
import { faCancel, faEdit, faSave, faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UUID } from '../../../../types/shared/UUID';
import { MobilityGroupApi } from '../../../../API/mobilityGroups/MobilityGroupApi';
import {
  convertToEditMobilityGroup,
  IEditMobilityGroup,
  IProgramTypeData,
  MobilityTransactionTypeData,
} from '../../../../types/mobility/IMobilityGroup';
import { Show } from '../../../shared/Show';
import { Spinner } from 'ui-toolkit';
import { Form, Formik } from 'formik';
import { MobilityHeaderTextField } from '../../../../tailwindComponents/textInputTW/MobilityHeaderTextField';
import { MobilityHeaderSelectField } from '../../../../tailwindComponents/selectTW/combobox/MobilityHeaderSelectField';
import { checkForMutationSuccess } from '../../../../API/checkForMutationSuccess';
import { MobilityAdditionButton } from './MobilityAdditionButton';
import { MobilityActionsButton } from './MobilityActionsButton';
import { MobilityOrdersButtons } from './MobilityOrdersButtons';
import { MobilityGroupEditionSchemaGenerator } from '../../../orderCreation/validation/MobilitySchema';
import { SelectOption } from '../../../../tailwindComponents/selectTW/interfaces';

export const MobilityHeader: React.FC<{
  currentOrderId: UUID;
  groupId: UUID;
  mobilityAdditionFeatureEnabled?: boolean;
  transactionTypes: MobilityTransactionTypeData[];
  programTypes: IProgramTypeData[];
}> = ({ groupId, currentOrderId, mobilityAdditionFeatureEnabled = false, transactionTypes, programTypes }) => {
  const {
    data: mobilityGroupWithOrders,
    isSuccess: mobilityGroupWithOrdersIsSuccess,
    isLoading: mobilityGroupWithOrdersIsLoading,
  } = MobilityGroupApi.useGetMobilityGroupByIdQuery(groupId);

  const [editMobilityGroup, { isLoading: isEditLoading }] = MobilityGroupApi.usePatchMobilityGroupMutation();

  const [showMobilityActionsMenu, setShowMobilityActionsMenu] = useState(false);
  const closeMobilityActionsMenu = () => setShowMobilityActionsMenu(false);
  const openMobilityActionsMenu = () => setShowMobilityActionsMenu(true);

  const [showAdditionMenu, setShowAdditionMenu] = useState(false);
  const closeAdditionMenu = () => setShowAdditionMenu(false);
  const openAdditionMenu = () => setShowAdditionMenu(true);

  const onClickMenuButton = () => {
    openMobilityActionsMenu();
  };

  const onClickAdditionButton = () => {
    openAdditionMenu();
  };

  useEffect(() => {
    // This closes the menu if it's open the user clicks outside of it
    if (showMobilityActionsMenu) {
      document.addEventListener('click', closeMobilityActionsMenu, { once: true, passive: true });
    }
    if (showAdditionMenu) {
      document.addEventListener('click', closeAdditionMenu, { once: true, passive: true });
    }
  }, [showMobilityActionsMenu, showAdditionMenu]);

  const [isEditingGroup, setIsEditingGroup] = useState<boolean>(false);

  const formInitialValues = useMemo(() => {
    if (mobilityGroupWithOrders) {
      return convertToEditMobilityGroup(mobilityGroupWithOrders);
    } else {
      return {
        id: '',
        externalId: undefined,
        programTypeId: null,
        takeTitle: null,
      } as IEditMobilityGroup;
    }
  }, [mobilityGroupWithOrders]);

  const onSubmit = async (formData: IEditMobilityGroup) => {
    const result = await editMobilityGroup(formData);
    if (
      checkForMutationSuccess(result, 'mobility group edition', 'Error submitting the mobility group modifications.')
    ) {
      setIsEditingGroup(false);
    }
  };

  if (mobilityGroupWithOrdersIsLoading) {
    return (
      <div className="flex w-full justify-between bg-green-50 p-3" data-test-id={'mobility-header'}>
        <div className="flex gap-6">
          <div className="flex">
            <Spinner size={SpinnerSize.regular} />
          </div>
        </div>
      </div>
    );
  }

  const programTypeOptions: SelectOption[] = programTypes
    .map((pType) => ({
      label: pType.label,
      value: pType.name,
    }))
    .concat({
      label: 'Not Applicable',
      value: null as any,
    });

  return (
    <div className="flex w-full justify-between bg-green-50 p-3" data-test-id={'mobility-header'}>
      <div className="flex gap-6">
        <div className="flex">
          <Show when={mobilityGroupWithOrdersIsSuccess}>
            <MobilityOrdersButtons orders={mobilityGroupWithOrders!!.mobilityOrders} currentOrderId={currentOrderId} />
          </Show>
        </div>
        <div className="relative">
          <MobilityAdditionButton
            featureEnabled={mobilityAdditionFeatureEnabled}
            showMenu={showAdditionMenu}
            onClick={onClickAdditionButton}
            transactionTypes={transactionTypes}
          />
        </div>
      </div>
      <Show when={!isEditingGroup}>
        <div className="flex gap-4 pr-2">
          <div>
            <span className="text-xs text-text-muted">External Group ID</span>{' '}
            <span className="text-xs text-foundation-primary">{mobilityGroupWithOrders!!.externalId}</span>
          </div>
          <div>
            <span className="text-xs text-foundation-primary text-text-muted">Program Type</span>{' '}
            <span className="text-xs text-foundation-primary">
              {mobilityGroupWithOrders!!.programType?.label || 'N/A'}
            </span>
          </div>
          <div>
            <span className="text-xs text-foundation-primary text-text-muted">Take Title?</span>{' '}
            <span className="text-xs text-foundation-primary">
              {takeTitleLabel(mobilityGroupWithOrders!!.takeTitle)}
            </span>
          </div>
          <div className="relative">
            <button onClick={() => setIsEditingGroup(!isEditingGroup)} data-test-id={'mobility-header-edit'}>
              <FontAwesomeIcon icon={faEdit} className="pr-2" />
            </button>
            <MobilityActionsButton
              onClick={onClickMenuButton}
              showMenu={showMobilityActionsMenu}
              featureEnabled={false}
            />
          </div>
        </div>
      </Show>
      <Show when={isEditingGroup}>
        <div className="flex gap-4 pr-2">
          <Formik
            initialValues={formInitialValues}
            onSubmit={onSubmit}
            validationSchema={MobilityGroupEditionSchemaGenerator()}
          >
            {({ handleReset }) => (
              <Form>
                <div className={'flex gap-2'}>
                  <MobilityHeaderTextField
                    name={'externalId'}
                    labelText={'External Group ID'}
                    className={'text-xs'}
                    isInlineLabel={true}
                    inputClassName={'px-2 py-1.5'}
                    labelClassName={'py-1'}
                    isRequired={true}
                    showErrorText={false}
                  />
                  <MobilityHeaderSelectField
                    name={'programTypeId'}
                    options={programTypeOptions}
                    labelText={'Program Type'}
                    className={'text-xs'}
                    isInlineLabel={true}
                    optionClassName={'text-xs py-0.5 px-1'}
                    buttonClassName={'py-1.5 pl-3 pr-10'}
                    labelClassName={'py-1'}
                  />
                  <MobilityHeaderSelectField
                    name={'takeTitle'}
                    options={[
                      { label: 'Yes', value: true },
                      { label: 'No', value: false },
                      { label: 'Not Applicable', value: null },
                    ]}
                    labelText={'Take Title?'}
                    className={'text-xs'}
                    isInlineLabel={true}
                    optionClassName={'text-xs py-0.5 px-1'}
                    buttonClassName={'py-1.5 pl-3 pr-10'}
                    labelClassName={'py-1'}
                  />
                  <div className="flex gap-1">
                    <Show when={!isEditLoading}>
                      <button type="submit" data-test-id={'mobility-header-edit-save'}>
                        <FontAwesomeIcon icon={faSave} className="pr-2" />
                      </button>
                      <button
                        onClick={() => {
                          handleReset();
                          setIsEditingGroup(false);
                        }}
                        data-test-id={'mobility-header-edit-cancel'}
                      >
                        <FontAwesomeIcon icon={faCancel} className="pr-2" />
                      </button>
                    </Show>
                    <Show when={isEditLoading}>
                      <div>
                        <FontAwesomeIcon icon={faSpinner} className="pr-2" />
                      </div>
                    </Show>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Show>
    </div>
  );
};

const takeTitleLabel = (takeTitle: boolean | undefined | null) => {
  switch (takeTitle) {
    case true:
      return 'Yes';
    case false:
      return 'No';
    default:
      return 'N/A';
  }
};
